import { SEDAN_MEDIO, perfilVeiculoPorId } from '../didatico/perfilVeiculo'
import { localParaMundo, mundoParaLocalDoObjeto } from './ancoragem'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * A corda não atravessa as coisas: contorna o volume do objeto, encostada nele.
 *
 * Cada objeto grande é uma caixa simplificada (no referencial dele), e o caminho
 * da corda em volta é o mais curto que não entra na caixa: a corda estica entre
 * as quinas, como na prática. Cada quina em que a corda dobra é um **ponto de
 * pressão** (aresta onde a fibra se desgasta), a menos que o objeto seja
 * arredondado (árvore, tronco) ou haja um protetor de corda ali.
 *
 * Só geometria, em TypeScript puro.
 */

type V = VetorTabletop

/** Folga entre a corda e a superfície (o raio da corda, mais um pouco). */
const MARGEM_M = 0.04
/** Pontas dentro da caixa (ponto de ancoragem na carroceria) saem pela face mais próxima. */
const TOLERANCIA_M = 0.005
/** A corda precisa dobrar mais que isto na quina para contar como ponto de pressão. */
export const ANGULO_MINIMO_PRESSAO_GRAUS = 20
/** Quinas a menos disto de uma ponta presa não contam como ponto de pressão. */
const RAIO_DA_PONTA_PRESA_M = 0.6
/** Um protetor de corda a menos que isto de uma quina a protege. */
export const ALCANCE_DO_PROTETOR_M = 0.45

interface Caixa {
  readonly centro: V
  readonly meia: V
  /** Tem quina viva? Árvore e tronco são arredondados: a corda contorna, mas sem aresta. */
  readonly aresta: boolean
}

/** Volumes simplificados, no referencial do objeto (o chão do objeto em y = 0). */
function caixaDoObjeto(objeto: ObjetoTabletop): Caixa | null {
  switch (objeto.tipo) {
    case 'carro':
    case 'suv': {
      const perfil = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : SEDAN_MEDIO
      const alto = objeto.tipo === 'suv' ? 1.08 : 1
      const h = perfil.altura * alto
      return { centro: [0, h / 2, 0], meia: [perfil.comprimento / 2, h / 2, (perfil.largura * alto) / 2], aresta: true }
    }
    case 'caminhao':
      return { centro: [0.1, 1.15, 0], meia: [2.5, 0.95, 1.07], aresta: true }
    case 'resgate':
      return { centro: [0, 1.4, 0], meia: [1, 0.9, 2.2], aresta: true }
    case 'caminhaoBombeiros':
      return { centro: [0, 1.5, 0], meia: [3.4, 1.5, 1.3], aresta: true }
    case 'moto':
      return { centro: [0, 0.5, 0], meia: [1.1, 0.5, 0.3], aresta: true }
    case 'muro':
      return { centro: [0, 0.8, 0], meia: [1.9, 0.8, 0.27], aresta: true }
    case 'arvore':
      return { centro: [0, 1.6, 0], meia: [0.36, 1.6, 0.36], aresta: false }
    default:
      return null
  }
}

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const lerp = (a: V, b: V, t: number): V => soma(a, esc(sub(b, a), t))

/** Os cantos da caixa e a posição dentro dela, em coordenadas locais já sem o centro. */
function dentro(p: V, meia: V, folga = 0): boolean {
  return Math.abs(p[0]) < meia[0] - folga && Math.abs(p[1]) < meia[1] - folga && Math.abs(p[2]) < meia[2] - folga
}

/** O segmento a→b atravessa o interior da caixa (centrada na origem)? Encostar na superfície não conta. */
function atravessa(a: V, b: V, meia: V): boolean {
  let t0 = 0
  let t1 = 1
  for (let k = 0; k < 3; k += 1) {
    const d = b[k]! - a[k]!
    const lim = meia[k]! - TOLERANCIA_M
    if (Math.abs(d) < 1e-12) {
      if (Math.abs(a[k]!) >= lim) return false
      continue
    }
    let ta = (-lim - a[k]!) / d
    let tb = (lim - a[k]!) / d
    if (ta > tb) [ta, tb] = [tb, ta]
    t0 = Math.max(t0, ta)
    t1 = Math.min(t1, tb)
    if (t0 >= t1) return false
  }
  return t1 - t0 > 1e-6
}

/** Empurra o ponto de dentro da caixa para a face mais próxima, já com a margem. */
function paraForaDaCaixa(p: V, meia: V): V {
  let melhor = 0
  let menor = Infinity
  for (let k = 0; k < 3; k += 1) {
    const folga = meia[k]! - Math.abs(p[k]!)
    if (folga < menor) {
      menor = folga
      melhor = k
    }
  }
  const saida: [number, number, number] = [p[0], p[1], p[2]]
  saida[melhor] = Math.sign(p[melhor]! || 1) * (meia[melhor]! + MARGEM_M)
  return saida
}

/**
 * O caminho mais curto de `a` até `b` (em coordenadas locais, caixa na origem)
 * sem entrar na caixa; só os pontos do meio. Vazio se o trecho reto já passa livre.
 */
function caminhoEmVolta(a: V, b: V, meiaBase: V): V[] {
  const meia: V = [meiaBase[0] + MARGEM_M, meiaBase[1] + MARGEM_M, meiaBase[2] + MARGEM_M]
  // o chão fica embaixo da caixa: a corda não passa por baixo
  const base = -meiaBase[1]
  if (!atravessa(a, b, meiaBase)) return []

  const nos: V[] = []
  const dxz = Math.hypot(b[0] - a[0], b[2] - a[2])
  // quinas verticais: a corda toca a quina na altura em que a reta de a até b passaria por ela
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const x = sx * meia[0]
      const z = sz * meia[2]
      const t = dxz > 1e-9 ? ((x - a[0]) * (b[0] - a[0]) + (z - a[2]) * (b[2] - a[2])) / (dxz * dxz) : 0.5
      const y = Math.min(meia[1], Math.max(base + MARGEM_M, a[1] + (b[1] - a[1]) * Math.min(1, Math.max(0, t))))
      nos.push([x, y, z])
      nos.push([x, meia[1], z])
    }
  }
  // por cima: onde a projeção do trecho entra e sai do retângulo de cima, na altura do topo
  const d = sub(b, a)
  const entradas: number[] = []
  for (const k of [0, 2]) {
    if (Math.abs(d[k]!) < 1e-12) continue
    for (const s of [-1, 1]) {
      const t = (s * meia[k]! - a[k]!) / d[k]!
      const q = lerp(a, b, t)
      const o = k === 0 ? 2 : 0
      if (t > 0 && t < 1 && Math.abs(q[o]!) <= meia[o]! + 1e-9) entradas.push(t)
    }
  }
  for (const t of entradas) {
    const q = lerp(a, b, t)
    nos.push([q[0], meia[1], q[2]])
  }

  // Dijkstra sobre a, b e os nós; uma aresta vale se não atravessa a caixa
  const pontos: V[] = [a, b, ...nos.filter((n) => !dentro(n, meiaBase, 0))]
  const dist = pontos.map(() => Infinity)
  const veio = pontos.map(() => -1)
  const visto = pontos.map(() => false)
  dist[0] = 0
  for (let passo = 0; passo < pontos.length; passo += 1) {
    let u = -1
    for (let i = 0; i < pontos.length; i += 1) if (!visto[i] && (u < 0 || dist[i]! < dist[u]!)) u = i
    if (u < 0 || dist[u] === Infinity) break
    visto[u] = true
    for (let v = 0; v < pontos.length; v += 1) {
      if (visto[v]) continue
      if (atravessa(pontos[u]!, pontos[v]!, meiaBase)) continue
      const custo = dist[u]! + comp(sub(pontos[v]!, pontos[u]!))
      if (custo < dist[v]!) {
        dist[v] = custo
        veio[v] = u
      }
    }
  }
  if (veio[1] === -1) return []
  const caminho: V[] = []
  for (let i = veio[1]!; i > 0; i = veio[i]!) caminho.unshift(pontos[i]!)
  return caminho
}

export interface PontoDePressao {
  readonly ponto: V
  readonly idObjeto: string
  /** Quanto a corda dobra nesta quina, em graus. */
  readonly anguloGraus: number
  /** Há um protetor de corda perto da quina. */
  readonly protegido: boolean
}

export interface TrechoContornado {
  /** Os pontos do meio, em ordem, de uma ponta do trecho à outra (sem repetir as pontas). */
  readonly intermediarios: readonly V[]
  /** Onde a ponta de saída ou chegada foi empurrada para fora do volume, se foi. */
  readonly de: V
  readonly para: V
  readonly pressoes: readonly PontoDePressao[]
}

const angulo = (a: V, b: V, c: V) => {
  const u = sub(a, b)
  const w = sub(c, b)
  const cos = (u[0] * w[0] + u[1] * w[1] + u[2] * w[2]) / (comp(u) * comp(w) || 1)
  return (Math.acos(Math.min(1, Math.max(-1, cos))) * 180) / Math.PI
}

/**
 * O trecho reto `de → para` (no mundo) com a corda contornando os objetos da
 * cena. Sem obstáculo no caminho, não há ponto intermediário.
 */
export function contornarTrecho(cena: CenaTabletop, de: V, para: V): TrechoContornado {
  let pontos: V[] = [de, para]
  const pressoes: PontoDePressao[] = []
  const protetores = cena.objetos.filter((o) => o.tipo === 'protetorCorda').map((o) => o.transformacao.posicao)

  for (const objeto of cena.objetos) {
    const caixa = caixaDoObjeto(objeto)
    if (!caixa) continue
    const aLocal = (p: V): V => sub(mundoParaLocalDoObjeto(p, objeto.transformacao), caixa.centro)
    const aMundo = (p: V): V => localParaMundo(soma(p, caixa.centro), objeto.transformacao)
    const novos: V[] = [pontos[0]!]
    for (let i = 1; i < pontos.length; i += 1) {
      let a = aLocal(novos[novos.length - 1]!)
      let b = aLocal(pontos[i]!)
      // ponto de ancoragem dentro da carroceria aproximada: a corda sai pela face mais próxima
      if (dentro(a, caixa.meia, TOLERANCIA_M)) {
        a = paraForaDaCaixa(a, caixa.meia)
        novos.push(aMundo(a))
      }
      let chegada: V | null = null
      if (dentro(b, caixa.meia, TOLERANCIA_M)) {
        b = paraForaDaCaixa(b, caixa.meia)
        chegada = aMundo(b)
      }
      const volta = caminhoEmVolta(a, b, caixa.meia)
      let anterior = a
      volta.forEach((v, k) => {
        const seguinte = k + 1 < volta.length ? volta[k + 1]! : b
        const mundo = aMundo(v)
        novos.push(mundo)
        const graus = 180 - angulo(anterior, v, seguinte)
        // junto da ponta presa a corda está amarrada na própria peça (a argola, o trilho): não é ponto de pressão
        const longeDasPontas = comp(sub(mundo, de)) > RAIO_DA_PONTA_PRESA_M && comp(sub(mundo, para)) > RAIO_DA_PONTA_PRESA_M
        if (graus >= ANGULO_MINIMO_PRESSAO_GRAUS && longeDasPontas) {
          pressoes.push({
            ponto: mundo,
            idObjeto: objeto.id,
            anguloGraus: graus,
            protegido: !caixa.aresta || protetores.some((p) => comp(sub(p, mundo)) <= ALCANCE_DO_PROTETOR_M),
          })
        }
        anterior = v
      })
      if (chegada) novos.push(chegada)
      novos.push(pontos[i]!)
    }
    pontos = novos
  }
  return {
    intermediarios: pontos.slice(1, -1),
    de,
    para,
    pressoes,
  }
}

/** Os trechos contornados de uma corda, um por trecho entre pontos consecutivos do percurso. */
export function contornosDaCorda(
  cena: CenaTabletop,
  corda: CordaTabletop,
  pontosNoMundo: ReadonlyArray<V | null>,
): ReadonlyArray<TrechoContornado | null> {
  const trechos: Array<TrechoContornado | null> = []
  for (let i = 1; i < pontosNoMundo.length; i += 1) {
    const a = pontosNoMundo[i - 1]
    const b = pontosNoMundo[i]
    if (!a || !b) {
      trechos.push(null)
      continue
    }
    trechos.push(contornarTrecho(cena, a, b))
  }
  return trechos
}
